// F-094 Actions groupées (messages et documents) et rapports mensuels par mandant.
import { useState } from "react";
import { post } from "@core/api";
import { dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Button, Card, Empty, ErrorBox, Input, Loading, Modal, Notice, PageHeader, Select, Table, Textarea, fieldError } from "@core/ui";
import { REGIMES, Status, docUrl, thisMonth, useOwnerOptions, useRun } from "./shared";

export function BulkPage() {
  const org = useOrg();
  const leases = useGet<any>(org.api("/pro/selectable-leases"));
  const docs = useGet<any>(org.api("/documents"));
  const history = useGet<any>(org.api("/pro/bulk-actions"));
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const [selected, setSelected] = useState<string[]>([]);
  const [form, setForm] = useState({ kind: "message", subject: "", body: "", document_id: "" });
  const [preview, setPreview] = useState<any>(null);
  const [open, setOpen] = useState<string | null>(null);
  const items: any[] = leases.data?.items ?? [];
  const toggle = (id: string) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const toSend = preview ? (preview.items as any[]).filter((i) => i.status === "a_envoyer" || i.status === "echec").length : 0;
  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Actions groupées"
        intro="Envoyez un message ou un document aux locataires de plusieurs baux. Vous voyez chaque destinataire et chaque montant avant l’envoi ; rien ne part sans votre confirmation."
      />
      <Card title="1. Sélection des baux">
        {leases.isLoading ? <Loading /> : null}
        {!items.length && !leases.isLoading ? <Empty title="Aucun bail actif">Les baux signés, actifs ou en préavis apparaissent ici.</Empty> : null}
        {items.length ? (
          <Table caption="Baux sélectionnables">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="Tout sélectionner"
                    checked={selected.length === items.length}
                    onChange={(e) => setSelected(e.target.checked ? items.map((l) => l.id) : [])}
                  />
                </th>
                <th>Logement</th>
                <th>Mandant</th>
                <th>Régime</th>
                <th>Locataires</th>
                <th className="num">Loyer et charges</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id}>
                  <td>
                    <input type="checkbox" aria-label={`Sélectionner ${l.property_label}`} checked={selected.includes(l.id)} onChange={() => toggle(l.id)} />
                  </td>
                  <td>{l.property_label}</td>
                  <td>{l.owner_name ?? "—"}</td>
                  <td>{REGIMES[l.regime] ?? l.regime}</td>
                  <td>
                    {l.tenants ?? "—"}
                    {l.tenants_with_email === 0 ? <span className="block small muted">Aucune adresse e-mail</span> : null}
                  </td>
                  <td className="num">{money(l.rent_cents + l.charges_cents)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
      <Card title="2. Contenu">
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            const body = { ...form, lease_ids: selected, document_id: form.kind === "document" ? form.document_id || null : null };
            const r: any = await run(() => post(org.api("/pro/bulk-actions"), body));
            if (r) {
              setPreview(r);
              invalidate(org.api("/pro/bulk-actions"));
            }
          }}
        >
          <Select
            label="Type d’envoi"
            value={form.kind}
            options={[
              { value: "message", label: "Message" },
              { value: "document", label: "Document" },
            ]}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
          />
          <Input label="Objet" required value={form.subject} error={fieldError(error, "subject")} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          {form.kind === "document" ? (
            <Select
              label="Document à envoyer"
              value={form.document_id}
              error={fieldError(error, "document_id")}
              help="Une copie est rattachée à chaque bail : le locataire la retrouve dans son espace."
              options={[{ value: "", label: "Choisir un document" }, ...(docs.data?.items ?? []).map((d: any) => ({ value: d.id, label: d.title || d.filename }))]}
              onChange={(e) => setForm({ ...form, document_id: e.target.value })}
            />
          ) : null}
          <Textarea
            label="Message"
            value={form.body}
            error={fieldError(error, "body")}
            help="Champs remplacés pour chaque destinataire : {locataire}, {logement}, {montant} (loyer et charges du bail)."
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
          <ErrorBox error={error && !fieldError(error, "subject") && !fieldError(error, "body") && !fieldError(error, "document_id") ? error : null} />
          <div>
            <Button type="submit" variant="secondaire" busy={busy} disabled={!selected.length}>
              Préparer l’aperçu ({selected.length} bail{selected.length > 1 ? "x" : ""})
            </Button>
          </div>
        </form>
      </Card>
      {preview ? (
        <Card title="3. Aperçu et envoi">
          <BulkItems view={preview} />
          {preview.sample_body ? (
            <Notice title="Exemple de message reçu">
              <span style={{ whiteSpace: "pre-wrap" }}>{preview.sample_body}</span>
            </Notice>
          ) : null}
          <Button
            busy={busy}
            disabled={toSend === 0}
            onClick={async () => {
              const r: any = await run(() => post(org.api(`/pro/bulk-actions/${preview.action.id}/execute`)), "Envoi effectué.");
              if (r) {
                setPreview(r);
                invalidate(org.api("/pro/bulk-actions"));
              }
            }}
          >
            {preview.action.status === "partiel" ? `Reprendre les ${toSend} envoi(s) en échec` : `Envoyer à ${toSend} destinataire(s)`}
          </Button>
        </Card>
      ) : null}
      <Card title="Historique">
        {history.data && !history.data.items.length ? <p className="muted">Aucune action groupée.</p> : null}
        {history.data?.items.length ? (
          <Table caption="Actions groupées">
            <thead>
              <tr>
                <th>Date</th>
                <th>Objet</th>
                <th>Statut</th>
                <th className="num">Envoyés</th>
                <th className="num">Non envoyés</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.data.items.map((a: any) => (
                <tr key={a.id}>
                  <td>{dateTime(a.created_at)}</td>
                  <td>{a.subject}</td>
                  <td>
                    <Status kind="bulkAction" value={a.status} />
                  </td>
                  <td className="num">{a.sent}</td>
                  <td className="num">{a.ignored + a.failed}</td>
                  <td className="right">
                    <Button variant="discret" small onClick={() => setOpen(a.id)}>
                      Détail
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
      {open ? <BulkDetail id={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function BulkItems({ view }: { view: any }) {
  return (
    <Table caption="Destinataires">
      <thead>
        <tr>
          <th>Logement</th>
          <th>Destinataire</th>
          <th className="num">Montant</th>
          <th>Statut</th>
        </tr>
      </thead>
      <tbody>
        {(view.items as any[]).map((i) => (
          <tr key={i.id}>
            <td>{i.property_label}</td>
            <td>
              {i.recipient_name || "—"}
              {i.recipient_email ? <span className="block muted small">{i.recipient_email}</span> : null}
            </td>
            <td className="num">{money(i.amount_cents)}</td>
            <td>
              <Status kind="bulk" value={i.status} />
              {i.reason ? <span className="block small">{i.reason}</span> : null}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function BulkDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const org = useOrg();
  const q = useGet<any>(org.api(`/pro/bulk-actions/${id}`));
  return (
    <Modal open onClose={onClose} title={q.data ? q.data.action.subject : "Action groupée"}>
      {q.isLoading ? <Loading /> : null}
      {q.data ? <BulkItems view={q.data} /> : null}
    </Modal>
  );
}

export function ReportsPage() {
  const org = useOrg();
  const owners = useOwnerOptions(org.orgId);
  const q = useGet<any>(org.api("/pro/reports"));
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const [month, setMonth] = useState(thisMonth());
  const [ownerId, setOwnerId] = useState("");
  const [batch, setBatch] = useState<any[] | null>(null);
  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Rapports mensuels"
        intro="Un rapport PDF par mandant : loyers appelés et encaissés, dépenses, tâches et incidents suivis sur ses logements. Il est partagé automatiquement sur son portail."
      />
      {org.can("operate") ? (
        <Card title="Générer">
          <div className="form-row">
            <Input label="Mois" type="month" value={month} error={fieldError(error, "month")} onChange={(e) => setMonth(e.target.value)} />
            <Select label="Mandant" value={ownerId} options={owners} onChange={(e) => setOwnerId(e.target.value)} />
          </div>
          <ErrorBox error={error && !fieldError(error, "month") ? error : null} />
          <div className="row">
            <Button
              variant="secondaire"
              busy={busy}
              disabled={!ownerId}
              onClick={async () => {
                if (await run(() => post(org.api("/pro/reports"), { owner_id: ownerId, month }), "Rapport généré et partagé.")) invalidate(org.api("/pro/reports"));
              }}
            >
              Générer pour ce mandant
            </Button>
            <Button
              busy={busy}
              onClick={async () => {
                const r: any = await run(() => post(org.api("/pro/reports/batch"), { month }), "Rapports générés.");
                if (r) {
                  setBatch(r.items);
                  invalidate(org.api("/pro/reports"));
                }
              }}
            >
              Générer pour tous les mandants
            </Button>
          </div>
          {batch ? (
            <ul className="list-plain">
              {batch.map((b) => (
                <li key={b.owner_id}>
                  {b.owner_name} : {b.status === "genere" ? "rapport généré" : b.status === "ignore" ? `ignoré (${b.reason})` : `échec (${b.reason})`}
                </li>
              ))}
            </ul>
          ) : null}
        </Card>
      ) : null}
      <Card title="Rapports">
        {q.isLoading ? <Loading /> : null}
        {q.data && !q.data.items.length ? <Empty title="Aucun rapport">Générez le rapport du mois écoulé pour vos mandants.</Empty> : null}
        {q.data?.items.length ? (
          <Table caption="Rapports">
            <thead>
              <tr>
                <th>Mandant</th>
                <th>Mois</th>
                <th className="num">Appelé</th>
                <th className="num">Encaissé</th>
                <th className="num">Dépenses</th>
                <th>Version</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((r: any) => (
                <tr key={r.id}>
                  <td>{r.owner_name}</td>
                  <td>{r.period_start.slice(0, 7)}</td>
                  <td className="num">{money(r.totals.loyers_appeles_cents)}</td>
                  <td className="num">{money(r.totals.loyers_encaisses_cents)}</td>
                  <td className="num">{money(r.totals.depenses_cents)}</td>
                  <td>
                    {r.version} · {dateTime(r.updated_at)}
                  </td>
                  <td className="right">
                    {r.document_id ? (
                      <a href={docUrl(r.document_id)} target="_blank" rel="noreferrer">
                        Ouvrir le PDF
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
    </div>
  );
}
